import React, { useState, useEffect, useContext, useCallback } from "react";
import ReactGA from "react-ga4";
import { useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import { makeStyles } from "@mui/styles";
import axios from "axios";
import { API_URL, ADMIN_URL } from "../../utils/constants";
import {
	Card,
	CardContent,
	Typography,
	Toolbar,
	Button,
	Grid,
	Divider,
	Chip,
	Modal,
	Tooltip,
	CircularProgress,
	RadioGroup,
	FormControlLabel,
	Radio,
	Alert,
} from "@mui/material";
import { UserContext } from "../../context/UserContext";
import { Check } from "tabler-icons-react";
import Logo from "../../assets/logo.png";
import { useTranslation } from "react-i18next";
import ServiceImage from "../../assets/book2.png";
import EcommerceImage from "../../assets/product12.png";
import WocommerceImage from "../../assets/productDetail1.png";
import i18n from "../../i18n/config";
import { useAlert } from "react-alert";

const useStyles = makeStyles({
	cardInfo: {
		display: "flex",
		width: "100%",
		justifyContent: "center",
	},
	cardPageInfo: {
		display: "flex",
		width: "100%",
		justifyContent: "center",
		alignItems: "center",
		flexDirection: "column",
		gap: 20,
	},
	logoutButton: {
		"&:hover": {
			backgroundColor: "white!important",
		},
	},
});

export default function Plans({ drawerWidth }) {
	const classes = useStyles();
	const { user } = useContext(UserContext);
	const [loading, setLoading] = useState(false);
	const [size, setSize] = useState(null);
	const [timesPublished, setTimesPublished] = useState(null);
	const [selectedApp, setSelectedApp] = useState(user?.app?.appType);
	const [selectedAppSubtype, setSelectedAppSubtype] = useState(null);
	const [showSelectAppModal, setShowSelectAppModal] = useState(false);
	const [showStripe, setShowStripe] = useState(false);
	const [selectAppButtonPressed, setSelectAppButtonPressed] = useState(false);
	const { t } = useTranslation();
	const alert = useAlert();
	const location = useLocation();

	const appSubtypes = {
		Ecommerce: ["Ecommerce", "Restaurant", "Other"],
		Woocommerce: ["Ecommerce", "Restaurant", "Other"],
		Services: ["Barber", "Salon", "Trainer", "Other"],
	};

	useEffect(() => {
		ReactGA.send({
			hitType: "pageview",
			page: window.location.href,
			title: "",
		});
	}, [location]);

	const getCouponLabel = useCallback(() => {
		if (user?.app?.appType === "Ecommerce") {
			return t("plans.couponEcommerce");
		} else if (user?.app?.appType === "Services") {
			return user?.subscription?.isOnlyWeb
				? t("plans.couponWeb")
				: t("plans.couponServices");
		} else if (user?.app?.appType === "Woocommerce") {
			return t("plans.couponWoocommerce");
		}
	}, [t, user?.app?.appType, user?.subscription?.isOnlyWeb]);

	const [label, setLabel] = useState(getCouponLabel());

	useEffect(() => {
		getCouponLabel();
	}, [getCouponLabel, t, user]);

	const getStatusColor = (status) => {
		switch (status) {
			case "trialing":
				return "skyblue";
			case "active":
				return "#43BD98";
			default:
				return "red";
		}
	};

	const getAppUsage = async () => {
		try {
			const res = await axios.get(ADMIN_URL + "appBytesUsage", {
				headers: { authorization: localStorage.getItem("token") },
			});
			setSize(res.data.size);
			setTimesPublished(res.data.times);
		} catch (error) {
			console.log(error);
		}
	};

	useEffect(() => {
		getAppUsage();
	}, []);

	const chooseApp = async () => {
		setLoading(true);
		try {
			await axios.patch(
				API_URL + "adminUsers",
				{
					chosenAppType: new Date().toISOString(),
					appType: selectedApp,
					appSubtype: selectedApp.includes("Services")
						? selectedAppSubtype &&
						  ["Barber", "Salon", "Trainer", "Other"].find(
								(t) => t === selectedAppSubtype
						  )
							? selectedAppSubtype
							: undefined
						: selectedApp.includes("Ecommerce") ||
						  selectedApp.includes("Woocommerce")
						? selectedAppSubtype &&
						  ["Ecommerce", "Restaurant", "Other"].find(
								(t) => t === selectedAppSubtype
						  )
							? selectedAppSubtype
							: undefined
						: undefined,
				},
				{
					headers: { authorization: localStorage.getItem("token") },
				}
			);
			window.location.href = window.location.origin;
		} catch (error) {
			setLoading(false);
			console.log(error);
		}
	};

	return (
		<Box
			component="main"
			sx={{
				...{
					flexGrow: 1,
					bgcolor: "background.default",
					p: 3,
				},
				...(drawerWidth
					? { width: { xs: "100%", sm: "calc(100vw - " + drawerWidth + "px)" } }
					: {}),
			}}
		>
			{drawerWidth && <Toolbar />}
			<Card variant="outlined">
				<>
					<CardContent>
						{user &&
						(showStripe ||
							!user.chosenAppType ||
							!user.subscription ||
							(user.subscription &&
								!["active", "past_due", "trialing"].find(
									(subscriptionStatus) =>
										subscriptionStatus === user.subscription.status
								))) ? (
							<>
								<Grid container sx={{ marginBottom: 2 }}>
									<Grid
										item
										xs={6}
										visibility={showStripe ? "hidden" : "visible"}
									>
										<img src={Logo} alt="logo" style={{ width: 90 }} />
									</Grid>
									<Grid
										item
										xs={6}
										sx={{ display: "flex" }}
										justifyContent="flex-end"
										visibility={showStripe ? "hidden" : "visible"}
									>
										<Button
											variant="text"
											sx={{ color: "#43BD98" }}
											onClick={() => {
												localStorage.removeItem("token");
												window.location.reload();
											}}
											size="small"
											className={classes.logoutButton}
										>
											{t("plans.logOut")}
										</Button>
									</Grid>
								</Grid>
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										margin: 2,
										alignItems: "center",
									}}
								>
									<Typography
										variant="body1"
										sx={{
											fontWeight: "700",
											fontSize: 16,
											textAlign: "center",
										}}
									>
										{user.subscription &&
										!["active", "past_due", "trialing"].find(
											(subscriptionStatus) =>
												subscriptionStatus === user.subscription.status
										) ? (
											<>
												{t("plans.noActivePlan")}{" "}
												<span
													style={{
														textDecoration: "underline",
														fontWeight: 500,
													}}
												>
													{user.email}
												</span>{" "}
												<span style={{ color: "#43bd98", fontWeight: "bold" }}>
													{t("plans.noActivePlan2")}
												</span>
											</>
										) : !user.chosenAppType ? (
											<>
												{t("plans.selectApp")}
												<br />
												<br />
											</>
										) : (
											<>
												{!showStripe && (
													<>
														{t("plans.accountExpired")}
														<br />
													</>
												)}
												{t("plans.accountSelect")}
												<br />
												<br />
												<Box
													sx={{
														marginBottom: 1,
														border: "1px solid #43BD98",
														padding: 3,
														borderRadius: 2,
													}}
												>
													<span
														style={{ fontWeight: "bold", color: "#ff5733" }}
													>
														{user?.subscription?.isOnlyWeb
															? "~24%"
															: selectedApp === "Services"
															? "~40%"
															: "~30%"}
													</span>{" "}
													{t("plans.discountText")}
													<Tooltip title={t("plans.copyTooltip")} arrow>
														<Chip
															label={label}
															size="small"
															onClick={() => {
																navigator.clipboard.writeText(getCouponLabel());
																setLabel(t("plans.copy"));
																setTimeout(
																	() => setLabel(getCouponLabel()),
																	1500
																);
															}}
															sx={{
																color: "white",
																backgroundColor: "#43bd98",
																marginLeft: 1,
																cursor: "pointer",
																"&:hover": {
																	backgroundColor: "#368e79",
																},
															}}
														/>
													</Tooltip>
													{""}*
													<Box
														sx={{
															marginTop: 2,
														}}
													>
														<Typography variant="body1" sx={{ fontSize: 12 }}>
															*{t("plans.discountInfo")}
														</Typography>
													</Box>
												</Box>
											</>
										)}
									</Typography>
									{user.subscription &&
										!["active", "past_due", "trialing"].find(
											(subscriptionStatus) =>
												subscriptionStatus === user.subscription.status
										) && (
											<Typography variant="body1">
												<b>{t("plans.noActivePlan3")}</b>
											</Typography>
										)}
									{(!user.chosenAppType || !user.subscription) && (
										<>
											{t("plans.emailCreated")}
											<Typography
												variant="h6"
												sx={{
													textOverflow: "ellipsis",
													overflow: "hidden",
													width: "100%",
													textAlign: "center",
													textTransform: "lowercase",
												}}
											>
												{user.email}
											</Typography>
										</>
									)}
								</Box>
								<Divider />
								{!user.chosenAppType || (!selectedApp && !user.subscription) ? (
									<>
										<Box
											sx={{
												display: "flex",
												flexDirection: "column",
												margin: 4,
												marginBottom: 0,
											}}
										>
											<Typography variant="body1" sx={{ textAlign: "center" }}>
												{t("plans.typeApp")}
											</Typography>
											<Box
												sx={{
													display: "flex",
													justifyContent: "center",
													alignItems: "flex-start",
													gap: 2,
													color: "#191A1A",
													marginTop: 2,
													flexDirection: {
														xs: "column",
														sm: "column",
														md: "row",
													},
												}}
											>
												<Box
													sx={{
														padding: 2,
														textAlign: "center",
														display: "flex",
														flexDirection: "column",
														gap: 2,
														maxWidth: "300px",
														alignItems: "center",
													}}
												>
													<img
														src={EcommerceImage}
														alt="ecommerce"
														style={{ width: 150 }}
													/>
													<Typography variant="h4" sx={{ textAlign: "center" }}>
														{t("plans.ecommerce")}
													</Typography>
													<ul
														style={{
															paddingLeft: 0,
															textAlign: "left",
															margin: 0,
															minHeight: 110,
														}}
													>
														<li>{t("plans.onlineShop")}</li>
														<li>{t("plans.restaurant")}</li>
														<li>{t("plans.cafe")}</li>
														<li>{t("plans.others")}</li>
													</ul>
													<Typography
														variant="body1"
														sx={{ fontWeight: 300, minHeight: 120 }}
													>
														{t("plans.ecommerceDesc")}
													</Typography>
													<Button
														variant="contained"
														color="primary"
														sx={{
															textTransform: "none",
															width: "100%",
															fontWeight: 500,
														}}
														fullWidth
														onClick={() => {
															setShowSelectAppModal(true);
															setSelectedAppSubtype(null);
															setSelectAppButtonPressed(false);
															setSelectedApp("Ecommerce");
														}}
													>
														{t("plans.ecommerceButton")}
													</Button>
												</Box>
												<Box
													sx={{
														padding: 2,
														textAlign: "center",
														display: "flex",
														flexDirection: "column",
														gap: 2,
														maxWidth: "300px",
														alignItems: "center",
													}}
												>
													<img
														src={WocommerceImage}
														alt="woocommerce"
														style={{ width: 150 }}
													/>
													<Typography variant="h4" sx={{ textAlign: "center" }}>
														{t("plans.woocommerce")}
													</Typography>
													<ul
														style={{
															paddingLeft: 0,
															textAlign: "left",
															margin: 0,
															minHeight: 110,
														}}
													>
														<li>{t("plans.onlineShop")}</li>
														<li>{t("plans.restaurant")}</li>
														<li>{t("plans.cafe")}</li>
														<li>{t("plans.others")}</li>
													</ul>
													<Typography
														variant="body1"
														sx={{ fontWeight: 300, minHeight: 120 }}
													>
														{t("plans.woocommerceDesc")}
													</Typography>
													<Button
														variant="contained"
														color="primary"
														sx={{
															textTransform: "none",
															width: "100%",
															fontWeight: 500,
														}}
														fullWidth
														onClick={() => {
															setShowSelectAppModal(true);
															setSelectedAppSubtype(null);
															setSelectAppButtonPressed(false);
															setSelectedApp("Woocommerce");
														}}
													>
														{t("plans.ecommerceButton")}
													</Button>
												</Box>
												<Box
													sx={{
														padding: 2,
														textAlign: "center",
														display: "flex",
														flexDirection: "column",
														gap: 2,
														maxWidth: "300px",
														alignItems: "center",
													}}
												>
													<img
														src={ServiceImage}
														alt="services"
														style={{ width: 150 }}
													/>
													<Typography variant="h4" sx={{ textAlign: "center" }}>
														{t("plans.services")}
													</Typography>
													<ul
														style={{
															paddingLeft: 0,
															textAlign: "left",
															margin: 0,
															minHeight: 110,
														}}
													>
														<li>{t("plans.barberShop")}</li>
														<li>{t("plans.beautySalon")}</li>
														<li>{t("plans.personalTrainer")}</li>
														<li>{t("plans.healthProfessional")}</li>
														<li>{t("plans.others")}</li>
													</ul>
													<Typography
														variant="body1"
														sx={{ fontWeight: 300, minHeight: 120 }}
													>
														{t("plans.servicesDesc")}
													</Typography>
													<Button
														variant="contained"
														color="primary"
														sx={{
															textTransform: "none",
															width: "100%",
															fontWeight: 500,
														}}
														fullWidth
														onClick={() => {
															setShowSelectAppModal(true);
															setSelectedAppSubtype(null);
															setSelectAppButtonPressed(false);
															setSelectedApp("Services");
														}}
													>
														{t("plans.ecommerceButton")}
													</Button>
												</Box>
											</Box>
										</Box>
									</>
								) : (
									<>
										<Box
											sx={{
												display: "flex",
												flexDirection: "column",
												margin: 4,
												marginBottom: 0,
											}}
										>
											<Box
												sx={{
													display: "flex",
													justifyContent:
														/*!user.subscription
														? "space-between"
														:*/ "center",
													margin: "auto",
													marginBottom: 2,
													alignItems: "center",
													width: 1000,
													maxWidth: "100%",
												}}
											>
												{/*!user.subscription && (
													<ArrowNarrowLeft
														onClick={() => {
															setSelectedApp(null);
														}}
														style={{
															marginRight: 5,
															color: "#43BD98",
															cursor: "pointer",
														}}
													/>
												)*/}
												<Typography variant="h4" sx={{ textAlign: "center" }}>
													{t("plans." + selectedApp?.toLowerCase())}
												</Typography>
												{/*!user.subscription && (
													<ArrowNarrowLeft
														style={{
															visibility: "hidden",
														}}
													/>
												)*/}
											</Box>
											<Typography variant="body1" sx={{ textAlign: "center" }}>
												{t("plans.selectOption")}
											</Typography>
											<Grid
												container
												spacing={2}
												sx={{
													maxWidth: 500,
													marginBottom: 4,
													marginTop: 0,
													marginRight: "auto",
													marginLeft: "auto",
												}}
											>
												{!user.subscription && (
													<Grid item xs={6}>
														<Typography
															variant="body2"
															sx={{
																display: "flex",
																alignItems: "center",
																justifyContent: "center",
																textAlign: "center",
															}}
														>
															<Check
																style={{ marginRight: 5, color: "#43BD98" }}
															/>{" "}
															{t("plans.trial")}
														</Typography>
													</Grid>
												)}
												<Grid item xs={!user.subscription ? 6 : 12}>
													<Typography
														variant="body2"
														sx={{
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															textAlign: "center",
														}}
													>
														<Check
															style={{ marginRight: 5, color: "#43BD98" }}
														/>{" "}
														{t("plans.cancel")}
													</Typography>
												</Grid>
											</Grid>
											<stripe-pricing-table
												pricing-table-id={
													selectedApp === "Ecommerce"
														? i18n.language === "es" ||
														  i18n.language.includes("es-") ||
														  i18n.language.includes("-ES") ||
														  i18n.language.includes("es_") ||
														  i18n.language.includes("_ES") ||
														  i18n.language === "ca" ||
														  i18n.language === "eu" ||
														  i18n.language === "gl"
															? user.subscription &&
															  user.subscriptionId !== "trial"
																? "prctbl_1PAYYfDWQtQU67uFla8efXl7"
																: "prctbl_1PAYXZDWQtQU67uFcQPtW9Pz"
															: //"prctbl_1NRIH2DWQtQU67uFJqZw1tr9"
															user.subscription &&
															  user.subscriptionId !== "trial"
															? "prctbl_1PAYVFDWQtQU67uFMxwwooGX"
															: "prctbl_1PAYQlDWQtQU67uFFBNCTpkU"
														: selectedApp === "Woocommerce"
														? i18n.language === "es" ||
														  i18n.language.includes("es-") ||
														  i18n.language.includes("-ES") ||
														  i18n.language.includes("es_") ||
														  i18n.language.includes("_ES") ||
														  i18n.language === "ca" ||
														  i18n.language === "eu" ||
														  i18n.language === "gl"
															? user.subscription &&
															  user.subscriptionId !== "trial"
																? "prctbl_1PAYduDWQtQU67uFtjIqBIA5"
																: "prctbl_1PAYcyDWQtQU67uFh5m63LN6"
															: //"prctbl_1NRIl2DWQtQU67uF5Jta9v15"
															user.subscription &&
															  user.subscriptionId !== "trial"
															? "prctbl_1PAYbNDWQtQU67uFzhD0dUYi"
															: "prctbl_1PAYa7DWQtQU67uFFF7iIV7F"
														: i18n.language === "es" ||
														  i18n.language.includes("es-") ||
														  i18n.language.includes("-ES") ||
														  i18n.language.includes("es_") ||
														  i18n.language.includes("_ES") ||
														  i18n.language === "ca" ||
														  i18n.language === "eu" ||
														  i18n.language === "gl"
														? user.subscription &&
														  user.subscriptionId !== "trial"
															? user?.subscription?.isOnlyWeb
																? "prctbl_1Q9QP2DWQtQU67uFMYEQEAQP"
																: "prctbl_1PAYhyDWQtQU67uFzocbrxoU"
															: user?.subscription?.isOnlyWeb
															? "prctbl_1Q9QP2DWQtQU67uFMYEQEAQP"
															: "prctbl_1PAYh9DWQtQU67uF0vrULO8M"
														: //"prctbl_1NRIf2DWQtQU67uFldY8F7Vh"
														user.subscription && user.subscriptionId !== "trial"
														? user?.subscription?.isOnlyWeb
															? "prctbl_1Q9QQdDWQtQU67uFmxzYUhIK"
															: "prctbl_1PAYfzDWQtQU67uFMyJluR1p"
														: user?.subscription?.isOnlyWeb
														? "prctbl_1Q9QQdDWQtQU67uFmxzYUhIK"
														: "prctbl_1PAYeyDWQtQU67uFiVWe7SNU"
												}
												//publishable-key="sk_test_51JlsDfDWQtQU67uFtdN7cP1diFFuCDZT9HiLP9Xt1Vz052Ka9zng1eMeRwZusw9yvgoqkcNoFi65EarrnlENHYaS00hFrm1Xo9"
												publishable-key="pk_live_51JlsDfDWQtQU67uFNJ2WEFXiXJx87wkAld5q4LMoyoe2P37OuCzheGGgpbYRPBe4jMBO7AGv3tt1Xv0Qavfzbot200uHQHhFE8"
												customer-email={user.email}
												//client-reference-id
											></stripe-pricing-table>
										</Box>
									</>
								)}
							</>
						) : (
							<>
								<Typography
									sx={{
										fontSize: 12,
										marginTop: 2,
										marginBottom: 1,
										fontWeight: 700,
										textTransform: "uppercase",
									}}
									color="text.secondary"
									gutterBottom
								>
									{t("plans.currentPlanTitle") ?? "Current plan"}
								</Typography>
								<div className={classes.cardPageInfo}></div>
								<Typography variant="body1" sx={{ marginBottom: 2 }}>
									{user.subscription.product.name}{" "}
									{user.subscription?.isOnlyWeb ? "(Web)" : ""}
									{user.subscriptionId === "trial" ? (
										<Chip
											label={t("plans.currentStatustrialing")}
											size="small"
											sx={{
												color: "white",
												backgroundColor: getStatusColor("trialing"),
												marginLeft: 1,
											}}
										/>
									) : user.subscription.cancel_at ? (
										<Chip
											label={t("plans.currentStatuscanceled")}
											size="small"
											sx={{
												color: "white",
												backgroundColor: getStatusColor("canceled"),
												marginLeft: 1,
											}}
										/>
									) : (
										<Chip
											label={t(
												"plans.currentStatus" + user.subscription.status
											)}
											size="small"
											sx={{
												color: "white",
												backgroundColor: getStatusColor(
													user.subscription.status
												),
												marginLeft: 1,
											}}
										/>
									)}
								</Typography>
								{!(user.subscriptionId === "trial") && (
									<Typography variant="h5" sx={{ marginBottom: 2 }}>
										{new Intl.NumberFormat(undefined, {
											style: "currency",
											currency: user.subscription.plan.currency,
										}).format(user.subscription.totalAmount / 100) +
											" " +
											t("plans." + user.subscription.plan.interval)}
										{user.subscription.totalAmount !==
											user.subscription.plan.amount &&
											(user.subscription.totalTaxes || 0) > 0 && (
												<small style={{ marginLeft: 5, fontSize: 10 }}>
													(
													{new Intl.NumberFormat(undefined, {
														style: "currency",
														currency: user.subscription.plan.currency,
													}).format(
														(user.subscription.totalAmount -
															(user.subscription.totalTaxes || 0)) /
															100
													)}{" "}
													{t("plans.noVat")})
												</small>
											)}
									</Typography>
								)}
								<Divider sx={{ margin: "20px 0px" }} />
								<Typography
									sx={{
										fontSize: 12,
										marginTop: 2,
										marginBottom: 1,
										fontWeight: 700,
										textTransform: "uppercase",
									}}
									color="text.secondary"
									gutterBottom
								>
									{t("plans.periodTitle") ?? "Current Period"}
								</Typography>
								<Typography variant="body1" sx={{ marginBottom: 2 }}>
									{user.subscriptionId === "trial" ? (
										<>
											{t("plans.afterEndTrial")}{" "}
											<b>
												{new Intl.DateTimeFormat(
													navigator.languages && navigator.languages.length
														? navigator.languages[0]
														: navigator.language,
													{
														dateStyle: "medium",
													}
												).format(
													new Date(user.subscription.current_period_end * 1000)
												)}
											</b>
											{t("plans.automatically")}
										</>
									) : user.subscription.cancel_at !== null ? (
										<>
											{t("plans.cancelled")}{" "}
											<b>
												{new Intl.DateTimeFormat(
													navigator.languages && navigator.languages.length
														? navigator.languages[0]
														: navigator.language,
													{
														dateStyle: "medium",
													}
												).format(new Date(user.subscription.cancel_at * 1000))}
											</b>
											.{" "}
											<Typography variant="body2">
												{t("plans.cancelledExplanation")}
											</Typography>
										</>
									) : (
										<>
											{t("plans.charged")}{" "}
											<b>
												{new Intl.DateTimeFormat(
													navigator.languages && navigator.languages.length
														? navigator.languages[0]
														: navigator.language,
													{
														dateStyle: "medium",
													}
												).format(
													new Date(user.subscription.current_period_end * 1000)
												)}
											</b>
										</>
									)}
								</Typography>
								<Divider sx={{ margin: "20px 0px" }} />
								{!(user.subscriptionId === "trial") && (
									<>
										<Typography
											sx={{
												fontSize: 12,
												marginTop: 2,
												marginBottom: 1,
												fontWeight: 700,
												textTransform: "uppercase",
											}}
											color="text.secondary"
											gutterBottom
										>
											{t("plans.storageTitle") ?? "Storage"}
										</Typography>

										<Typography variant="body1" sx={{ marginBottom: 2 }}>
											{t("plans.storage")}{" "}
											<b>
												{new Intl.NumberFormat(undefined, {
													style: "decimal",
													maximumFractionDigits: 2,
												}).format((size ?? 0) * 0.000001) + "MB"}
												/2GB
											</b>
											.
											<br />
											<small>
												{t("plans.maximumStorage")}{" "}
												<a href="mailto:info@applaby.com">info@applaby.com</a>
											</small>
										</Typography>
										<Divider sx={{ margin: "20px 0px" }} />
										<Typography
											sx={{
												fontSize: 12,
												marginTop: 2,
												marginBottom: 1,
												fontWeight: 700,
												textTransform: "uppercase",
											}}
											color="text.secondary"
											gutterBottom
										>
											{t("plans.publishedTimesTitle") ?? "Published times"}
										</Typography>
										<Typography variant="body1" sx={{ marginBottom: 2 }}>
											{t("plans.timesPublished")} <b>{timesPublished ?? 0}/5</b>{" "}
											{t("plans.times")} .
											<br />
										</Typography>
										<Divider sx={{ margin: "20px 0px 40px" }} />
									</>
								)}

								{user.subscriptionId === "trial" ? (
									<Button
										variant="contained"
										color="primary"
										sx={{ width: "auto", marginBottom: 1 }}
										onClick={() => {
											setShowStripe(true);
										}}
									>
										{t("plans.activate")}
									</Button>
								) : (
									<>
										<Button
											variant="contained"
											color="primary"
											sx={{ width: "auto", marginBottom: 1 }}
											onClick={() => {
												window
													.open(
														"https://billing.stripe.com/p/login/bIYeYu2ea7Dd3Ic8ww?customer_email=" +
															user.email,
														"_blank"
													)
													.focus();
											}}
										>
											{t("plans.manage")}*
										</Button>
										<br />
										<small>{t("plans.manageExplanation")}</small>
									</>
								)}
								{user.subscription?.isOnlyWeb && (
									<>
										<Divider sx={{ margin: "20px 0px 40px" }} />
										<Typography
											variant="body1"
											component="p"
											sx={{ marginTop: 4, marginBottom: 4 }}
										>
											{t("plans.upgrade")}{" "}
											<a href="mailto:info@applaby.com">info@applaby.com</a>
										</Typography>
									</>
								)}
							</>
						)}
					</CardContent>
				</>
			</Card>
			<Modal
				open={showSelectAppModal}
				onClose={() => {
					setShowSelectAppModal(false);
				}}
			>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						width: { xs: "90%", sm: "80%", md: 600 },
						maxWidth: "100%",
						bgcolor: "background.paper",
						boxShadow: 24,
						p: 4,
						maxHeight: "calc(100% - 80px)",
						overflowY: "auto",
						overflowX: "hidden",
					}}
				>
					<Typography id="modal-modal-title" variant="h6" component="h2">
						{t("plans.appSelection")}
					</Typography>
					<Divider
						sx={{
							marginLeft: "-30px",
							maxWidth: "calc(100% + 60px)",
							width: "calc(100% + 60px)",
							marginTop: 2,
						}}
					/>

					<Typography
						id="modal-modal-title"
						variant="body1"
						component="p"
						sx={{ marginTop: 4, marginBottom: 4 }}
					>
						{t("plans.chosenApp")}{" "}
						<b>{t("plans." + selectedApp?.toLowerCase())}</b>.
						<br />
						<br />
						{t("plans.chosenAppNoChange")}
					</Typography>

					<Divider
						sx={{
							marginLeft: "-30px",
							maxWidth: "calc(100% + 60px)",
							width: "calc(100% + 60px)",
							marginTop: 2,
						}}
					/>

					<Typography
						variant="body1"
						component="p"
						sx={{ marginTop: 4, marginBottom: 2, fontWeight: 500 }}
					>
						{t("plans.subTypeSelection")}
					</Typography>

					<RadioGroup
						aria-labelledby=""
						value={selectedAppSubtype}
						name="radio-buttons-group"
						onChange={(e) => {
							setSelectedAppSubtype(e.target.value);
						}}
						sx={{
							marginBottom: !(!selectedAppSubtype && selectAppButtonPressed)
								? 8
								: 0,
						}}
					>
						{appSubtypes[selectedApp]?.map((subtype) => (
							<>
								<FormControlLabel
									key={subtype}
									value={subtype}
									control={<Radio />}
									label={t("plans.subType" + subtype)}
								/>
							</>
						))}
					</RadioGroup>

					{!selectedAppSubtype && selectAppButtonPressed && (
						<Alert
							severity="error"
							sx={{
								marginBottom: 2,
								"& .MuiAlert-icon": {
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
								},
							}}
						>
							{t("plans.selectAppSubtype")}
						</Alert>
					)}

					<Divider
						sx={{
							marginLeft: "-30px",
							maxWidth: "calc(100% + 60px)",
							width: "calc(100% + 60px)",
							marginTop: 2,
						}}
					/>
					<Box
						sx={{
							display: "flex",
							justifyContent: "flex-end",
							alignItems: "center",
							marginTop: 4,
						}}
					>
						<Button
							sx={{ width: "auto", marginLeft: 1 }}
							variant="outlined"
							onClick={() => {
								setShowSelectAppModal(false);
							}}
							size="medium"
						>
							{t("plans.cancelButton")}
						</Button>
						<Button
							sx={{ width: "auto", marginLeft: 1 }}
							variant="contained"
							onClick={async () => {
								if (selectedAppSubtype) {
									await chooseApp();
								} else {
									setSelectAppButtonPressed(true);
								}
							}}
							size="medium"
						>
							{loading ? <CircularProgress size={12} /> : t("plans.continue")}
						</Button>
					</Box>
				</Box>
			</Modal>
		</Box>
	);
}
